/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import styled from 'styled-components';
import { navigate } from 'gatsby';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { encodeFormData } from '@rsc/utils/encodeFormData';
import axios from 'axios';
import breakpoints from '@rsc/ui-components/breakpoints';
import Button from '@rsc/ui-components/button/Button';
import {
  StyledForm,
  FormRow,
  ValidationMessage,
} from '@components/forms/StyledForm';

const FormWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 1em;
`;

const RecaptchaWrapper = styled.div`
  margin: 0 0 1rem;
  @media screen and (max-width: ${breakpoints.sm}) {
    max-width: 200px;
    transform: scale(0.77);
    transform-origin: 0 0;
  }
`;

const validationSchema = yup.object().shape({
  fullName: yup
    .string()
    .trim()
    .required('Full name is required')
    .label('Full Name'),
  phoneNumber: yup.string().label('Phone number'),
  email: yup
    .string()
    .email()
    .required('Please use a valid email address, such as user@example.com.')
    .label('Email'),
  orderNumber: yup.string().label('Order Number'),
  rmaNumber: yup.string().label('RMA Number'),
  questionComment: yup
    .string()
    .trim()
    .required('You must enter your question.')
    .label('Comments/Questions'),
});

const ContactForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [recaptchaVerification, setRecaptchaVerification] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitButtonDisabled, disableSubmitButton] = useState(false);

  const onSubmit = (formData, e) => {
    e.preventDefault();
    if (recaptchaVerification) {
      disableSubmitButton(true);
      const data = encodeFormData({
        'form-name': 'Contact Us',
        ...formData,
        'g-recaptcha-response': recaptchaVerification,
      });

      axios({
        method: 'POST',
        url: '/',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data,
      })
        .then(() => {
          navigate('/contact-us/success/');
        })
        .catch(() => {
          navigate('/contact-us/fail/');
        });
    }
  };

  const submitButtonClicked = () => {
    setFormSubmitted(true);
  };

  const recaptchaVerifyCallback = response => {
    setRecaptchaVerification(response);
  };

  return (
    <FormWrapper>
      <StyledForm
        onSubmit={handleSubmit(onSubmit)}
        name="Contact Us"
        method="POST"
        data-netlify="true"
        data-netlify-recaptcha="true"
      >
        <input type="hidden" name="form-name" value="Contact Us" />

        <FormRow width="60" invalid={errors.fullName}>
          <label htmlFor="fullName">
            Full Name <span className="asterisk"> *</span>
            <input type="text" {...register('fullName')} id="fullName" />
          </label>
          <ValidationMessage>
            {errors.fullName && errors.fullName.message}
          </ValidationMessage>
        </FormRow>

        <FormRow width="60" invalid={errors.phoneNumber}>
          <label htmlFor="phoneNumber">
            Phone Number
            <input type="tel" id="phoneNumber" {...register('phoneNumber')} />
          </label>
          <ValidationMessage>
            {errors.phoneNumber && errors.phoneNumber.message}
          </ValidationMessage>
        </FormRow>

        <FormRow width="60" invalid={errors.email}>
          <label htmlFor="email" required>
            Email Address <span className="asterisk"> *</span>
            <input type="email" id="email" {...register('email')} />
          </label>
          <ValidationMessage>
            {errors.email && errors.email.message}
          </ValidationMessage>
        </FormRow>

        <FormRow width="60" invalid={errors.orderNumber}>
          <label htmlFor="orderNumber">
            Order Number
            <input id="orderNumber" {...register('orderNumber')} />
          </label>
          <ValidationMessage>
            {errors.orderNumber && errors.orderNumber.message}
          </ValidationMessage>
        </FormRow>

        <FormRow width="60" invalid={errors.rmaNumber}>
          <label htmlFor="rmaNumber">
            RMA Number
            <input id="rmaNumber" {...register('rmaNumber')} />
          </label>
          <ValidationMessage>
            {errors.rmaNumber && errors.rmaNumber.message}
          </ValidationMessage>
        </FormRow>

        <FormRow width="60" invalid={errors.questionComment}>
          <label htmlFor="questionComment">
            Comments/Questions <span className="asterisk"> *</span>
            <textarea id="questionComment" {...register('questionComment')} />
          </label>
          <ValidationMessage>
            {errors.questionComment && errors.questionComment.message}
          </ValidationMessage>
        </FormRow>

        <RecaptchaWrapper>
          {process.env.GATSBY_KURGO_RECAPTCHA_KEY && (
            <ReCAPTCHA
              sitekey={process.env.GATSBY_KURGO_RECAPTCHA_KEY}
              onChange={recaptchaVerifyCallback}
            />
          )}
        </RecaptchaWrapper>

        <FormRow width="60">
          {formSubmitted && !recaptchaVerification && (
            <ValidationMessage>
              Please check the box to prove you are not a robot!
            </ValidationMessage>
          )}
        </FormRow>

        <FormRow>
          <Button
            type="submit"
            onClick={submitButtonClicked}
            disabled={submitButtonDisabled}
            mobileFullWidth
          >
            Submit Form
          </Button>
        </FormRow>
      </StyledForm>
    </FormWrapper>
  );
};

export default ContactForm;
